import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2, Paperclip, X, Pencil, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'started_exercising', label: 'Started exercising' },
  { id: 'changed_jobs',       label: 'Changed jobs' },
  { id: 'moved',              label: 'Moved' },
  { id: 'started_habit',      label: 'Started a habit' },
  { id: 'stopped_habit',      label: 'Stopped a habit' },
  { id: 'health_test',        label: 'Took a health test' },
  { id: 'reached_goal',       label: 'Reached a goal' },
  { id: 'started_learning',   label: 'Started learning something' },
  { id: 'life_event',         label: 'Important life event' },
  { id: 'custom',             label: 'Something else' },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

const today = () => new Date().toISOString().slice(0, 10);

// Group events by year for chronological display
const groupByYear = (events) => {
  const groups = new Map();
  for (const e of events) {
    const year = (e.date || '').slice(0, 4);
    if (!groups.has(year)) groups.set(year, []);
    groups.get(year).push(e);
  }
  return Array.from(groups.entries()); // preserves insertion order (newest first, thanks to backend sort)
};

const formatDate = (iso) => {
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const emptyDraft = () => ({
  title: '',
  date: today(),
  category: 'life_event',
  note: '',
  attachment_url: '',
});

const Timeline = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Edit mode: which event is currently being edited, and the
  // in-flight edit draft (only one event can be edited at a time).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/timeline`);
      setEvents(r.data?.events || []);
    } catch {
      toast.error('Could not load your timeline.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => groupByYear(events), [events]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      toast.error('Give it a short title.');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/timeline`, {
        title: draft.title.trim(),
        date: draft.date,
        category: draft.category,
        note: draft.note.trim() || null,
        attachment_url: draft.attachment_url.trim() || null,
      });
      setEvents((prev) => {
        const next = [r.data, ...prev];
        next.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        return next;
      });
      setDraft(emptyDraft());
      setShowForm(false);
      toast.success('Added.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save.');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (event_id) => {
    setDeletingId(event_id);
    try {
      await axios.delete(`${API}/timeline/${event_id}`);
      setEvents((prev) => prev.filter((e) => e.event_id !== event_id));
      // If we were editing this event, close the edit view.
      if (editingId === event_id) {
        setEditingId(null);
        setEditDraft(null);
      }
    } catch {
      toast.error('Could not delete.');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Edit flow ─────────────────────────────────────────
  const startEdit = (ev) => {
    setEditingId(ev.event_id);
    setEditDraft({
      title: ev.title || '',
      date: ev.date || '',
      category: ev.category || 'custom',
      note: ev.note || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async (event_id) => {
    if (!editDraft) return;
    if (!editDraft.title.trim()) {
      toast.error('Give it a short title.');
      return;
    }
    setSavingEdit(true);
    try {
      const r = await axios.patch(`${API}/timeline/${event_id}`, {
        title: editDraft.title.trim(),
        date: editDraft.date,
        category: editDraft.category,
        note: editDraft.note.trim() || null,
      });
      // Merge the updated event back into state and re-sort.
      setEvents((prev) => {
        const next = prev.map((e) => (e.event_id === event_id ? r.data : e));
        next.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        return next;
      });
      setEditingId(null);
      setEditDraft(null);
      toast.success('Saved.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save.');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AppShell>
      <SEO title="Timeline" description="Your private, chronological Summaros timeline." path="/timeline" noindex />

      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-20 animate-fade-up" data-testid="timeline-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-3">Timeline</p>
            <h1
              className="display-serif text-4xl sm:text-5xl text-forest text-balance"
              style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 360" }}
              data-testid="timeline-title"
            >
              Life happened. Add it.
            </h1>
            <p className="mt-3 text-[15px] text-ink-muted max-w-md">
              Private by default. Nothing here ever shows up in Community.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary"
              data-testid="timeline-add"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Add an event
            </button>
          )}
        </div>

        {/* ── Add form ────────────────────────────── */}
        {showForm && (
          <form
            onSubmit={submit}
            className="rounded-2xl border border-line-strong bg-cream-soft p-6 sm:p-8 mb-10 animate-fade-up"
            data-testid="timeline-form"
          >
            <div className="flex items-center justify-between mb-5">
              <p className="eyebrow">New event</p>
              <button
                type="button"
                onClick={() => { setShowForm(false); setDraft(emptyDraft()); }}
                className="text-ink-muted hover:text-forest"
                data-testid="timeline-form-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid sm:grid-cols-[1fr,180px] gap-4 mb-4">
              <div>
                <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Title</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="A short line. What happened?"
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px]"
                  maxLength={120}
                  required
                  data-testid="timeline-input-title"
                />
              </div>
              <div>
                <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Date</label>
                <input
                  type="date"
                  value={draft.date}
                  onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px]"
                  required
                  data-testid="timeline-input-date"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Category</label>
              <div className="flex flex-wrap gap-2" data-testid="timeline-categories">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, category: c.id }))}
                    className={`px-3 py-1.5 rounded-full text-[13px] border transition-colors
                      ${draft.category === c.id
                        ? 'bg-forest text-cream border-forest'
                        : 'bg-cream text-ink-muted border-line hover:border-forest'
                      }`}
                    data-testid={`timeline-cat-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Note (optional)</label>
              <textarea
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                rows={3}
                maxLength={2000}
                placeholder="Anything you'd want to remember about this."
                className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px] resize-none"
                data-testid="timeline-input-note"
              />
            </div>

            <div className="mb-6">
              <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Attachment (optional URL)</label>
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-ink-muted shrink-0" strokeWidth={1.5} />
                <input
                  type="url"
                  value={draft.attachment_url}
                  onChange={(e) => setDraft((d) => ({ ...d, attachment_url: e.target.value }))}
                  placeholder="https://…"
                  className="flex-1 px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[14px]"
                  data-testid="timeline-input-attachment"
                />
              </div>
              <p className="mt-1 text-[11px] text-ink-faint">Paste a link to a photo or document. Actual file uploads are coming.</p>
            </div>

            <div className="flex items-center gap-3">
              <button type="submit" disabled={creating} className="btn-primary" data-testid="timeline-form-submit">
                {creating ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Saving…</> : 'Add to timeline'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setDraft(emptyDraft()); }}
                className="text-[14px] text-ink-muted hover:text-forest transition-colors"
                data-testid="timeline-form-cancel"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* ── List ─────────────────────────────────── */}
        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-6 h-6 text-forest animate-spin mx-auto" strokeWidth={1.5} />
          </div>
        ) : events.length === 0 && !showForm ? (
          <div className="rounded-2xl border border-dashed border-line-strong bg-cream-soft py-12 px-6 text-center" data-testid="timeline-empty">
            <img
              src="/art/tree.png"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="mx-auto mb-4 w-40 h-auto select-none"
              data-testid="timeline-empty-art"
            />
            <p className="text-ink-muted mb-1">Nothing here yet.</p>
            <p className="text-[15px] text-ink">Life happened. Add it.</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary mt-6"
              data-testid="timeline-empty-add"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Add your first event
            </button>
          </div>
        ) : (
          <div className="space-y-14" data-testid="timeline-list">
            {grouped.map(([year, group]) => (
              <div key={year || 'unknown'}>
                <div className="flex items-center gap-4 mb-6">
                  <p className="font-display text-3xl text-forest" style={{ fontVariationSettings: "'opsz' 72, 'SOFT' 40, 'wght' 380" }}>
                    {year || '—'}
                  </p>
                  <div className="flex-1 h-px bg-line" />
                  <span className="text-[12px] font-mono text-ink-faint">{group.length} {group.length === 1 ? 'event' : 'events'}</span>
                </div>
                <div className="relative pl-6 border-l border-line-strong space-y-8">
                  {group.map((ev) => {
                    const isEditing = editingId === ev.event_id;
                    return (
                    <article
                      key={ev.event_id}
                      className="relative"
                      data-testid={`timeline-event-${ev.event_id}`}
                    >
                      <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-clay border-2 border-cream" />

                      {isEditing ? (
                        /* ── Edit view ─────────────────── */
                        <div
                          className="rounded-xl border border-line-strong bg-cream-soft p-5 sm:p-6 animate-fade-up"
                          data-testid={`timeline-edit-${ev.event_id}`}
                        >
                          <div className="flex items-center justify-between mb-4">
                            <p className="eyebrow">Editing</p>
                          </div>

                          <div className="grid sm:grid-cols-[1fr,180px] gap-4 mb-4">
                            <div>
                              <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Title</label>
                              <input
                                type="text"
                                value={editDraft.title}
                                onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                                className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px]"
                                maxLength={120}
                                data-testid={`timeline-edit-title-${ev.event_id}`}
                              />
                            </div>
                            <div>
                              <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Date</label>
                              <input
                                type="date"
                                value={editDraft.date}
                                onChange={(e) => setEditDraft((d) => ({ ...d, date: e.target.value }))}
                                className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px]"
                                data-testid={`timeline-edit-date-${ev.event_id}`}
                              />
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Category</label>
                            <div className="flex flex-wrap gap-2">
                              {CATEGORIES.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => setEditDraft((d) => ({ ...d, category: c.id }))}
                                  className={`px-3 py-1.5 rounded-full text-[13px] border transition-colors
                                    ${editDraft.category === c.id
                                      ? 'bg-forest text-cream border-forest'
                                      : 'bg-cream text-ink-muted border-line hover:border-forest'
                                    }`}
                                  data-testid={`timeline-edit-cat-${ev.event_id}-${c.id}`}
                                >
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <label className="block text-[12px] font-mono uppercase tracking-widest text-ink-muted mb-2">Note (optional)</label>
                            <textarea
                              value={editDraft.note}
                              onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                              rows={3}
                              maxLength={2000}
                              className="w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-[15px] resize-none"
                              data-testid={`timeline-edit-note-${ev.event_id}`}
                            />
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-line">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                onClick={() => saveEdit(ev.event_id)}
                                disabled={savingEdit}
                                className="btn-primary"
                                data-testid={`timeline-edit-save-${ev.event_id}`}
                              >
                                {savingEdit ? (
                                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Saving…</>
                                ) : (
                                  <><Save className="w-4 h-4" strokeWidth={1.5} /> Save changes</>
                                )}
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="btn-secondary"
                                data-testid={`timeline-edit-cancel-${ev.event_id}`}
                              >
                                Cancel
                              </button>
                            </div>
                            <button
                              onClick={() => remove(ev.event_id)}
                              disabled={deletingId === ev.event_id}
                              className="inline-flex items-center gap-2 text-[14px] text-clay hover:text-clay-soft transition-colors"
                              data-testid={`timeline-edit-delete-${ev.event_id}`}
                            >
                              {deletingId === ev.event_id
                                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
                              Delete event
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── View / read-only card ─────── */
                        <button
                          type="button"
                          onClick={() => startEdit(ev)}
                          className="group block w-full text-left rounded-lg -mx-2 px-2 py-1 hover:bg-cream-soft transition-colors"
                          data-testid={`timeline-open-${ev.event_id}`}
                          aria-label={`Edit ${ev.title}`}
                        >
                          <div className="flex items-baseline justify-between gap-4 mb-1">
                            <p className="font-display text-xl text-forest" style={{ fontVariationSettings: "'opsz' 36, 'SOFT' 40, 'wght' 460" }}>
                              {ev.title}
                            </p>
                            <span
                              className="text-ink-faint group-hover:text-forest transition-colors shrink-0 inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest"
                              data-testid={`timeline-edit-icon-${ev.event_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                              Edit
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-muted mb-3">
                            <span>{formatDate(ev.date)}</span>
                            <span className="text-ink-faint">·</span>
                            <span className="font-mono uppercase tracking-widest text-[11px]">{CATEGORY_LABEL[ev.category] || ev.category}</span>
                          </div>
                          {ev.note && (
                            <p className="text-[15px] leading-[1.6] text-ink mb-3 whitespace-pre-wrap text-pretty">{ev.note}</p>
                          )}
                          {ev.attachment_url && (
                            <a
                              href={ev.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 text-[13px] text-clay hover:text-forest transition-colors"
                              data-testid={`timeline-attachment-${ev.event_id}`}
                            >
                              <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
                              Attachment
                            </a>
                          )}
                        </button>
                      )}
                    </article>
                  );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </AppShell>
  );
};

export default Timeline;
